import { Injectable, Signal, inject, signal } from '@angular/core';
import { Client, IMessage, ReconnectionTimeMode, StompSubscription } from '@stomp/stompjs';
import { Observable, Subject } from 'rxjs';
import { ResultStatus, RunnerPipelineStep } from '../models/test-run.model';
import { ReportTestItem, ReportTestStatusCounts } from '../dashboard/ci-mapping/ci-mapping.model';
import { StompAccessTokenService } from './stomp-access-token.service';
import { stompBrokerUrl, withBearerHeader } from './stomp-broker';
import { LiveFeedConnection } from '../models/live-feed-connection.model';
import type { ExecutionJobStatus } from './execution-job.service';

/** Die Drahtnamen des Servers (`TestRunLiveEventType`). */
export type TestRunLiveEventType =
  | 'step-updated'
  | 'test-completed'
  | 'pipeline-completed'
  | 'report-test-updated'
  | 'report-tests-replaced'
  | 'pipeline-failure-categorized'
  | 'pipeline-steps-updated'
  | 'runner-job-updated'
  | 'call-expanded';

/** Die Zeile eines live gemeldeten Tests; der Server sendet leere Felder als `null` mit. */
export type ReportTestLiveEntry = Omit<ReportTestItem, 'linkedBugKey'>;

/** Die Zaehler der Live-Zeilen eines Durchlaufs nach der Meldung. */
export interface ReportTestLiveCounts {
  statusCounts: ReportTestStatusCounts;
  total: number;
  matched: number;
  unmatched: number;
  reportedTotal: number | null;
}

/**
 * Nutzlast des Live-Feeds (NEX-1041). Ein Satz Felder fuer alle Ereignisse;
 * was fuer das jeweilige nicht gilt, fehlt in der Nachricht.
 */
export interface TestRunLiveEvent {
  type: TestRunLiveEventType;
  executionId: string;
  caseResultId?: string;
  stepResultId?: string;
  status?: ResultStatus;
  stepsPassed?: number;
  stepsTotal?: number;
  durationMs?: number;
  reportTest?: ReportTestLiveEntry;
  counts?: ReportTestLiveCounts;
  /** Bei `pipeline-steps-updated` die vollstaendige Schritt-Liste, nicht nur die geaenderten. */
  pipelineSteps?: RunnerPipelineStep[];
  /** Bei `runner-job-updated` der neue Zustand des Rechner-Auftrags zum Fall (NEX-1752). */
  jobStatus?: ExecutionJobStatus;
  at: string;
}

const RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * STOMP-Client fuer den Live-Feed eines Durchlaufs (NEX-1043). Abonniert das Topic
 * aus {@code TestRunLiveFeedPublisher.topicFor} und reicht die Ereignisse durch.
 *
 * <p>Ein Dienst je Ansicht statt einer wurzelweiten Instanz: der Feed gehoert genau
 * einem Durchlauf, und beim Verlassen der Seite soll die Verbindung fallen, statt
 * fuer den naechsten Durchlauf weiterzulaufen.
 *
 * <p>Der Wiederaufbau liegt im Client selbst ({@code reconnectTimeMode}); eine
 * eigene Retry-Schleife daneben wuerde dieselbe Verbindung abwechselnd aufbauen
 * und abreissen. Was der Aufrufer davon braucht, ist {@link connection}.
 */
@Injectable()
export class TestlaufLiveFeedService {
  private readonly accessToken = inject(StompAccessTokenService);

  /** Ueberschreibbarer Seam fuer Tests — echte Verbindung nur im Betrieb. */
  clientFactory: (brokerUrl: string) => Client = (brokerUrl) =>
    new Client({
      brokerURL: brokerUrl,
      reconnectDelay: RECONNECT_DELAY_MS,
      maxReconnectDelay: MAX_RECONNECT_DELAY_MS,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
    });

  private readonly connectionState = signal<LiveFeedConnection>('connecting');
  readonly connection: Signal<LiveFeedConnection> = this.connectionState.asReadonly();

  private readonly eventSubject = new Subject<TestRunLiveEvent>();
  readonly events: Observable<TestRunLiveEvent> = this.eventSubject.asObservable();

  private client: Client | null = null;
  private subscription: StompSubscription | null = null;

  connect(projectId: string, runId: string, executionId: string): void {
    if (this.client) {
      return;
    }
    this.connectionState.set('connecting');
    const topic = `/topic/projects/${projectId}/test-runs/${runId}/executions/${executionId}`;
    const client = this.clientFactory(stompBrokerUrl());
    withBearerHeader(client, () => this.accessToken.acquire());
    // Das Abonnement haengt am Verbindungsaufbau und nicht an `activate`: nach
    // jedem Reconnect ist die alte Subscription des Brokers fort, und ohne ein
    // neues bliebe die Seite still verbunden, ohne je wieder etwas zu empfangen.
    client.onConnect = () => {
      if (this.client !== client) {
        return;
      }
      this.connectionState.set('connected');
      this.subscription = client.subscribe(topic, (message: IMessage) => this.dispatch(message));
    };
    // Ein abgegebener Client meldet sich nach `deactivate` womoeglich noch spaet;
    // er darf weder den Zustand noch das Abonnement seines Nachfolgers anfassen.
    client.onWebSocketClose = () => {
      if (this.client === client) {
        this.subscription = null;
        this.connectionState.set('lost');
      }
    };
    client.onStompError = () => {
      if (this.client === client) {
        this.connectionState.set('lost');
      }
    };
    this.client = client;
    client.activate();
  }

  disconnect(): void {
    const client = this.client;
    this.client = null;
    this.subscription?.unsubscribe();
    this.subscription = null;
    void client?.deactivate();
  }

  /**
   * Ein unlesbarer Rahmen kostet sein Ereignis, nicht den Feed: das Abonnement
   * bleibt stehen und der naechste Test laeuft wieder ein.
   */
  private dispatch(message: IMessage): void {
    try {
      this.eventSubject.next(JSON.parse(message.body) as TestRunLiveEvent);
    } catch {
      return;
    }
  }
}
